import { debounce } from '../../utils/debounce'
import { BaseComponent } from '../../components/base-component'
import { $el } from 'src/scripts/utils/utils'

const ANNOUNCEMENT_SELECTOR = '[data-scrolling-text-line]'
const LINE_LINK_SELECTOR = '[data-scrolling-text-anchor]'
const ANIMATION_CLASS = 'scrolling-text--animated'
const FONT_MIXED_ATTRIBUTE = 'with-font-mixed'
const FONT_OUTLINE_CLASS = 'scrolling-text__text--font-outline'

export class ScrollingTextLine extends BaseComponent {
  announcement: Node
  initialScreenWidth: number = 0
  isWithFontMixed: boolean
  screenSizeMultiply: number = 2
  resizeObserver: ResizeObserver

  constructor() {
    super()
    this.isWithFontMixed = this.hasAttribute(FONT_MIXED_ATTRIBUTE)
  }

  mountComponent(): void {
    this.resizeObserver = new ResizeObserver(this.handleResize)
    this.resizeObserver.observe(document.body)
  }

  unmountComponent(): void {
    this.resizeObserver.disconnect()
  }

  getDimentions = (): { screenWidth: number; announcementWidth: number } => {
    const announcement = $el(ANNOUNCEMENT_SELECTOR, this)

    if (!announcement) {
      return
    }

    const screenWidth = document.body.clientWidth
    const announcementWidth = announcement.clientWidth

    this.initialScreenWidth = screenWidth

    return { screenWidth, announcementWidth }
  }

  calcQuantityToRender = (
    screenWidth: number,
    announcementWidth: number,
  ): number => {
    const blocksToRender = Math.ceil(
      (screenWidth * this.screenSizeMultiply) / announcementWidth,
    )

    return blocksToRender % 2 === 0 ? blocksToRender : blocksToRender + 1
  }

  renderNodes = (blocksNumberToRender: number): void => {
    if (!this.announcement) {
      this.announcement = $el(ANNOUNCEMENT_SELECTOR, this).cloneNode(true)
    }

    const announcement = this.announcement.cloneNode(true)
    const link = $el(LINE_LINK_SELECTOR, this)

    if (!announcement) {
      return
    }

    const nodes = this.isWithFontMixed
      ? this.getFragmentWithMixedFont(blocksNumberToRender, announcement)
      : this.getFragment(blocksNumberToRender, announcement)

    if (link) {
      nodes.appendChild(link)
    }

    this.replaceChildren(nodes)
  }

  getFragment = (blocksNumberToRender, announcement): DocumentFragment => {
    const fragment = document.createDocumentFragment()
    const announcements = []

    for (let i = 0; i < blocksNumberToRender; i++) {
      const clonedAnnouncement = announcement.cloneNode(true)
      let newClass = ANIMATION_CLASS

      if (this.isWithFontMixed && i % 2 === 0) {
        newClass += ` ${FONT_OUTLINE_CLASS}`
      }

      clonedAnnouncement.classList.add(newClass)

      announcements.push(clonedAnnouncement)
    }

    fragment.replaceChildren(...announcements)

    return fragment
  }

  getFragmentWithMixedFont = (
    blocksNumberToRender,
    announcement,
  ): DocumentFragment => {
    const fragment = document.createDocumentFragment()
    const announcements = []

    for (let i = 0; i < blocksNumberToRender - 1; i++) {
      const clonedAnnouncement1 = announcement.cloneNode(true)
      const clonedAnnouncement2 = announcement.cloneNode(true)
      const div = document.createElement('div')

      clonedAnnouncement1.classList.add(FONT_OUTLINE_CLASS)

      div.replaceChildren(clonedAnnouncement1, clonedAnnouncement2)
      div.classList.add(ANIMATION_CLASS)

      announcements.push(div)
    }

    fragment.replaceChildren(...announcements)

    return fragment
  }

  handleResize = debounce((): void => {
    const screenWidth = document.body.clientWidth

    if (screenWidth > this.initialScreenWidth) {
      this.render()
    }

    this.setAttribute('data-is-rendered', 'true')
  }, 100)

  render = (): void => {
    const { screenWidth, announcementWidth } = this.getDimentions()
    const blocksNumberToRender = this.calcQuantityToRender(
      screenWidth,
      announcementWidth,
    )

    this.renderNodes(blocksNumberToRender)
  }
}
